import * as React from 'react';
import * as moment from 'moment';
import Grid from '@material-ui/core/Grid';
import FormHelperText from '@material-ui/core/FormHelperText';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import Paper from '@material-ui/core/Paper';
import { renderDateTimeEdit, renderTextFieldWithSubmitButton } from '../../common/forms/FieldCreators';
import { localizations } from '../../localization/localization';
import { FilterInfo, LocationNameModel, FilterMode, UsedFilterSettingsModel, getEmptyFilterSettingsModel, FilterSettingsModel, FilterInfoExtraData, Reporter } from '../../common/models/CommonModels';
import { SettingsProperties } from '../../account/models/SettingsModels';
import Tooltip from '@material-ui/core/Tooltip';
import saveIcon from '../../images/ic_save_as.svg';
import { FilterSettingsState } from '../../account/store/FilterSettingsStore';
import AddEditFilterSetting from '../../account/forms/AddEditFilterSetting';
import EmailIcon from '@material-ui/icons/Email';
import CancelIcon from '@material-ui/icons/Cancel';
import { debounce } from 'ts-debounce';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as locationApi from '../../api/locationsApi';

interface FilterPanelProps {
    filterIncludes: { trucks: boolean, units: boolean };
    filterValues: {
        reporters: Reporter[]
    },
    initialValues?: { reportNumberSearch?: string },
    filterSettings: FilterSettingsState,
    onFiltersChanged: (filters: FiltersState) => void;
    onSaveFilterCancel: () => void;
    onSaveFilterSubmit: (values: FilterSettingsModel) => void;
    updateSaveFilterDestinationCities: (data: string[]) => void;
    updateSaveFilterDepartureCities: (data: string[]) => void;
    updateNewFilterSettingsReporters: (data: string[]) => void;
    onSaveFilter: (data?: FilterSettingsModel, afterAddOrEdit?: () => void) => void;
}

type FiltersState = FilterInfoExtraData & {
    selectedFilterSetting: FilterSettingsModel | null;
};

type FilterPanelState = FiltersState & {
    allCities: LocationNameModel[];
}

const truckProperties = {
    ["adr"]: localizations.trucks.properties.adr,
    ["thermo"]: localizations.trucks.properties.thermal,
    ["glass"]: localizations.trucks.properties.glass,
    ["tank"]: localizations.trucks.properties.tank,
    ["mega"]: localizations.trucks.properties.mega,
    ["explosives"]: localizations.trucks.properties.explosive,
    ["container20"]: localizations.trucks.properties.container20,
    ["containerOver20"]: localizations.trucks.properties.containerOver20,
    ["pieceGoods"]: localizations.trucks.properties.pieceGoods,
    ["bulkGoods"]: localizations.trucks.properties.bulkGoods,
}

const unitProperties = {
    ["trailer"]: localizations.unitTypes.trailer,
}

const minDate: moment.Moment = moment().subtract(12, "months").startOf("day");

class FilterPanel extends React.Component<FilterPanelProps, FilterPanelState> {
    private debouncedHandleFiltersChanged = debounce(this.handleFiltersChanged, 2000, { isImmediate: false });

    constructor(props: FilterPanelProps) {
        super(props);

        const start = moment().startOf("day");

        const filterString = localStorage.getItem("commonFilters");

        let stateFilters: FilterPanelState = {
            filterMode: FilterMode.All,
            fromDate: start,
            toDate: start.clone().add(7, "days"),
            fromCities: [] as string[],
            toCities: [] as string[],
            properties: [] as string[],
            reporter: [] as string[],
            selectedReporters: [],
            reportNumberSearch: props.initialValues && props.initialValues.reportNumberSearch || "",
            error: "",
            selectedFilterSetting: null,
            fromCityLocations: [],
            toCityLocations: [],
            allCities: [],
        };

        if (filterString) {
            const filters = this.copyFilterValuesOver(stateFilters);
            stateFilters = Object.assign({}, stateFilters, filters);
        }

        stateFilters = { ...this.getFilterSettingsModelIfEnabled(stateFilters), allCities: [] };

        this.state = stateFilters;

        this.handleToCityChange = this.handleToCityChange.bind(this);
        this.handlePropertiesChange = this.handlePropertiesChange.bind(this);
        this.handleReporterChange = this.handleReporterChange.bind(this);
        this.handleFromDateChange = this.handleFromDateChange.bind(this);
        this.handleFromCityChange = this.handleFromCityChange.bind(this);
        this.handleToDateChange = this.handleToDateChange.bind(this);
        this.handlePropertiesSubmit = this.handlePropertiesSubmit.bind(this);
        this.handleFilterModeChange = this.handleFilterModeChange.bind(this);
        this.handleReportNumberChange = this.handleReportNumberChange.bind(this);
        this.handleReportNumberSubmit = this.handleReportNumberSubmit.bind(this);
        this.saveFilter = this.saveFilter.bind(this);
        this.isSaveFilterEnabled = this.isSaveFilterEnabled.bind(this);
        this.renderFilterSelectItems = this.renderFilterSelectItems.bind(this);
        this.handleSelectFilter = this.handleSelectFilter.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.clearFilter = this.clearFilter.bind(this);
        this.renderFilterSettingSelectValue = this.renderFilterSettingSelectValue.bind(this);
    }

    public componentDidMount() {
        const filterString = localStorage.getItem("commonFilters");

        locationApi.getLocationNames().then(data => {
            this.setState({
                allCities: data
            })
        })

        const start = moment().startOf("day");
        const stateFilters: FilterInfoExtraData = {
            filterMode: FilterMode.All,
            fromDate: start,
            toDate: start.clone().add(7, "days"),
            fromCities: [],
            toCities: [],
            properties: [],
            reporter: [],
            selectedReporters: [],
            reportNumberSearch: this.props.initialValues && this.props.initialValues.reportNumberSearch || "",
            error: "",
            fromCityLocations: [],
            toCityLocations: []
        };

        if (filterString) {
            const filters = this.copyFilterValuesOver(stateFilters);
            this.setState(filters);
        }

        this.props.onFiltersChanged(this.state);
    }

    public componentDidUpdate(prevProps: FilterPanelProps){
        if (this.props.filterValues.reporters !== prevProps.filterValues.reporters) {
            this.setState({
                selectedReporters: this.getReportersFromIds(this.state.reporter)
            });
        }
    }

    public render() {
        const { filterValues } = this.props;
        const { allCities } = this.state;
        const adding = this.props.filterSettings.isEditing;
        const { reporters } = this.props.filterValues;
        const editedItem = this.props.filterSettings.editedItem;
        const isSaveEnabled = this.isSaveFilterEnabled();

        return <Grid container direction="row" spacing={3} justify="flex-start">
            <Grid item>
                <FormControl style={{ minWidth: 120 }}>
                    <InputLabel shrink id="viewMode">{localizations.actions.show}</InputLabel>
                    <Select
                        id="viewMode"
                        value={this.state.filterMode}
                        onChange={this.handleFilterModeChange}
                    >
                        <MenuItem value={FilterMode.All}>{localizations.common.allSelection}</MenuItem>
                        <MenuItem value={FilterMode.Company}>{localizations.company.ownCompany}</MenuItem>
                        <MenuItem value={FilterMode.Own}>{localizations.company.personalAd}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item>
                {renderTextFieldWithSubmitButton({
                    label: localizations.common.generalSearchField,
                    placeholder: "",
                    width: "170px",
                    defaultValue: this.state.reportNumberSearch,
                    value: this.state.reportNumberSearch,
                    onChange: this.handleReportNumberChange,
                    onSubmit: this.handleReportNumberSubmit,
                    onBlur: this.handleReportNumberSubmit
                })}
            </Grid>
            <Grid item>
                {renderDateTimeEdit({ label: localizations.common.startTime, value: this.state.fromDate, onChange: debounce(this.handleFromDateChange, 2000, { isImmediate: false }), onAccept: this.handleFromDateChange, minDate })}
            </Grid>
            <Grid item >
                {renderDateTimeEdit({ label: localizations.common.endTime, value: this.state.toDate, onChange: debounce(this.handleToDateChange, 2000, { isImmediate: false }), onAccept: this.handleToDateChange, minDate })}
                {this.state.error ? (
                    <FormHelperText error aria-describedby="component-error-text" id="component-error-text">
                        {localizations.validation.endingTimeError}
                    </FormHelperText>
                ) : null}
            </Grid>
            <Grid item>
                <FormControl style={{ minWidth: 120 }}>
                    <Autocomplete
                        id="fromCity"
                        multiple
                        size="small"
                        options={allCities}
                        getOptionSelected={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        style={{ width: 300, height: 50, zIndex: 1000 }}
                        limitTags={3}
                        renderInput={(params) => (
                            <TextField {...params} style={{ backgroundColor: "#F7F8F9" }} InputLabelProps={{ shrink: true }} variant="standard" label={localizations.common.pointOfDeparture} placeholder="" />
                        )}
                        value={this.state.fromCityLocations}
                        onChange={this.handleFromCityChange}
                    />
                </FormControl>
            </Grid>
            <Grid item>
                <FormControl style={{ minWidth: 120 }}>
                    <Autocomplete
                        id="toCity"
                        multiple
                        size="small"
                        options={allCities}
                        getOptionSelected={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        style={{ width: 300, height: 50, zIndex: 1000 }}
                        limitTags={3}
                        renderInput={(params) => (
                            <TextField {...params} style={{ backgroundColor: "#F7F8F9" }} InputLabelProps={{ shrink: true }} variant="standard" label={localizations.common.pointOfArrival} placeholder="" />
                        )}
                        value={this.state.toCityLocations}
                        onChange={this.handleToCityChange}
                    />
                </FormControl>
            </Grid>
            <Grid item>
                <FormControl style={{ minWidth: 100 }}>
                    <InputLabel shrink id="properties">{localizations.common.properties}</InputLabel>
                    <Select
                        id="properties"
                        autoWidth
                        multiple
                        value={this.state.properties}
                        renderValue={this.renderPropertiesSelectValue}
                        onChange={this.handlePropertiesChange}
                        MenuProps={{
                            onExit: this.handlePropertiesSubmit
                        }}
                    >
                        {Object.keys(truckProperties).map((item, index) => (<MenuItem key={item} value={item}>
                            <Checkbox checked={this.state.properties.indexOf(item) > -1} />
                            <ListItemText primary={truckProperties[item]} />
                        </MenuItem>))}
                        {Object.keys(unitProperties).map((item, index) => (
                            <MenuItem key={item} value={item}>
                                <Checkbox checked={this.state.properties.indexOf(item) > -1} />
                                <ListItemText primary={unitProperties[item]} />
                            </MenuItem>)
                        )}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item>
                <FormControl style={{ minWidth: 100 }}>
                    <Autocomplete
                        id="reportedBy"
                        multiple
                        size="small"
                        options={filterValues.reporters}
                        getOptionLabel={(option) => option.name}
                        style={{ width: 300, height: 50, zIndex: 1000 }}
                        limitTags={3}
                        renderInput={(params) => (
                            <TextField {...params} style={{ backgroundColor: "#F7F8F9" }} InputLabelProps={{ shrink: true }} variant="standard" label={localizations.common.reporter} placeholder="" />
                        )}
                        value={this.state.selectedReporters}
                        onChange={this.handleReporterChange}
                    />
                </FormControl>
            </Grid>
            <Grid item style={{ minWidth: 100 }}>
                <Grid container direction="column" justify="center" alignItems="center">
                    <InputLabel shrink id="loadLabel">{localizations.common.filter}</InputLabel>
                    <Select
                        id="filtersList"
                        autoWidth
                        value={[this.state.selectedFilterSetting?.id]}
                        renderValue={this.renderFilterSettingSelectValue}
                        onChange={this.handleFilterChange} >
                        {this.props.filterSettings.data ? this.renderFilterSelectItems(this.props.filterSettings.data) : null}
                    </Select>
                </Grid>
            </Grid>
            <Grid item>
                <Grid container direction="row" justify="flex-end" alignItems="baseline">
                    {isSaveEnabled ? (
                        <span>
                            <Tooltip title={localizations.actions.save}><img src={saveIcon} onClick={this.saveFilter} style={{ paddingLeft: 5, paddingRight: 5, paddingTop: 8 }} alt="SaveFilter" /></Tooltip>
                        </span>
                    ) : null}
                    <Tooltip title={localizations.actions.delete}><CancelIcon onClick={this.clearFilter} style={{ paddingLeft: 5, paddingTop: 12 }} /></Tooltip>
                </Grid>
            </Grid>
            {adding ? (
                <AddEditFilterSetting
                    isOpen={adding}
                    isEditMode={false}
                    editMode={this.props.filterSettings.editMode}
                    onCancel={this.props.onSaveFilterCancel}
                    onSubmit={this.props.onSaveFilterSubmit}
                    filterValues={{ reporters }}
                    editedItem={editedItem}
                    updateDestinationCities={this.props.updateSaveFilterDestinationCities}
                    updateDepartureCities={this.props.updateSaveFilterDepartureCities}
                    updateReporters={this.props.updateNewFilterSettingsReporters}
                />) : null}
        </Grid>
            ;
    }

    private handleReportNumberSubmit() {
        this.debouncedHandleFiltersChanged(this.state);
    }

    private handleReportNumberChange(event: React.ChangeEvent<HTMLInputElement>) {
        const searchText: string = event.target.value;

        this.setState({
            reportNumberSearch: searchText
        });
    }

    private reviveFilters(key: string, value: any) {
        if (key === "toDate" || key === "fromDate") {
            return moment(value);
        }

        return value;
    }

    private handleFiltersChanged(filters: FiltersState) {
        localStorage.setItem("commonFilters", JSON.stringify(filters));

        this.disableUsedFilterSettingsModel();

        this.props.onFiltersChanged(filters);
    }

    private handleFilterModeChange(event: React.ChangeEvent<HTMLSelectElement>) {

        const mode = event.target.value as FilterMode;

        this.setState({
            filterMode: mode
        });

        const customState = Object.assign({}, this.state, { filterMode: event.target.value });

        this.handleFiltersChanged(customState);
    }

    private handleFromCityChange(event: any, value: any[]) {
        let values: string[] = [];
        let locationValues: LocationNameModel[] = [];
        if (value !== undefined || value !== null) {
            values = value.map((item, index) => item.id);
            locationValues = value.map((item, index) => item);
        }
        this.setState({
            fromCities: values,
            fromCityLocations: locationValues
        })

        const customState = Object.assign({}, this.state, { fromCities: values, fromCityLocations: locationValues });
        this.debouncedHandleFiltersChanged(customState);
    }

    private handleFromDateChange(value: moment.Moment) {
        if (value === undefined || value === null || !value.isValid()) { return; }

        if (minDate.isAfter(value)) { return; }

        let toDate = this.state.toDate;
        if (value.isAfter(toDate)) {
            toDate = value;
        }

        this.setState({
            fromDate: value,
            toDate
        });

        const customState = Object.assign({}, this.state, { fromDate: value, toDate });

        this.handleFiltersChanged(customState);
    }

    private handleToDateChange(value: moment.Moment) {
        if (value === undefined || value === null || !value.isValid()) { return; }

        if (minDate.isAfter(value)) { return; }

        let fromDate = this.state.fromDate;

        if (fromDate.isAfter(value)) {
            fromDate = value;
        }

        this.setState({
            toDate: value,
            fromDate
        });

        const customState = Object.assign({}, this.state, { toDate: value, fromDate });
        this.handleFiltersChanged(customState);
    }

    private renderPropertiesSelectValue(selected: any[]) {
        return selected.map(item => truckProperties[item] || unitProperties[item]).join(', ')
    }

    private renderFilterSettingSelectValue(selected: any[]) {
        return this.state.selectedFilterSetting?.name;
    }

    private handleToCityChange(event: any, value: any[]) {
        let values: string[] = [];
        let locationValues: LocationNameModel[] = [];
        if (value !== undefined || value !== null) {
            values = value.map((item, index) => item.id);
            locationValues = value.map((item, index) => item);
        }
        this.setState({
            toCities: values,
            toCityLocations: locationValues
        })

        const customState = Object.assign({}, this.state, { toCities: values, toCityLocations: locationValues });
        this.debouncedHandleFiltersChanged(customState);
    }

    private handlePropertiesChange(event: any) {
        this.setState({
            properties: event.target.value
        });
    }

    private handlePropertiesSubmit(event: any) {
        this.handleFiltersChanged(this.state);
    }

    // Set reporter value to state.
    private handleReporterChange(event: any, value: Reporter[]) {
        this.setState({
            reporter: value.map(reporter => reporter.id),
            selectedReporters: value
        }, () => this.handleFiltersChanged(this.state));
    }

    private getReportersFromIds(reporters: string[]): Reporter[]{
        return this.props.filterValues.reporters.filter(reporter => reporters.includes(reporter.id));
    }

    private disableUsedFilterSettingsModel() {
        const currFiltterSettingsModel = localStorage.getItem("usedFilterSettingsModel");
        if (currFiltterSettingsModel) {
            const currSettings = JSON.parse(currFiltterSettingsModel);
            if (currSettings && currSettings.isInUse) {
                currSettings.isInUse = false;
                localStorage.setItem("usedFilterSettingsModel", JSON.stringify(currSettings));
            }
        }
    }

    private getFilterSettingsModelIfEnabled(prevValues: FiltersState): FiltersState {
        const currFiltterSettingsModel = localStorage.getItem("usedFilterSettingsModel");
        if (currFiltterSettingsModel) {
            const currSettings = JSON.parse(currFiltterSettingsModel);
            let usedModel: UsedFilterSettingsModel | null = null;
            if (currSettings) {
                usedModel = currSettings as UsedFilterSettingsModel;
            }
            if (usedModel && usedModel.isInUse) {
                const stateFilters = {
                    filterMode: prevValues.filterMode,
                    fromDate: prevValues.fromDate,
                    toDate: prevValues.toDate,
                    fromCities: usedModel.model.departureCityIds,
                    toCities: usedModel.model.destinationCityIds,
                    properties: this.convertPropertiesToAnyArray(usedModel.model.properties),
                    reporter: usedModel.model.reporters,
                    selectedReporters: this.getReportersFromIds(usedModel.model.reporters),
                    reportNumberSearch: usedModel.model.generalSearchField,
                    error: prevValues.error,
                    selectedFilterSetting: usedModel.model,
                    fromCityLocations: usedModel.model.fromCityLocations ?? [],
                    toCityLocations: usedModel.model.toCityLocations ?? []
                };
                return stateFilters;
            }
        }
        return prevValues;
    }


    private renderFilterSelectItems(items: FilterSettingsModel[]) {
        return items ? items.map((item, index) => {
            return (
                <MenuItem key={item.id} value={item.id}>
                    {item.name} {item.tracking ? (
                        <EmailIcon style={{ fill: "blue", paddingLeft: 3 }} />
                    ) : null}
                </MenuItem>)
        }) : null;
    }

    // Set filter value to state.
    private handleFilterChange(event: any) {
        this.handleSelectFilter(event.target.value);
    }

    private saveFilter() {
        const filterInfo = this.state;
        this.handleSaveWithInitialValues(filterInfo);
    }

    private handleSaveWithInitialValues(filterInfo: FilterInfo) {
        const filterSettings: FilterSettingsModel = getEmptyFilterSettingsModel();
        filterSettings.name = "";
        filterSettings.tracking = false;
        filterSettings.departureCityIds = filterInfo.fromCities;
        filterSettings.destinationCityIds = filterInfo.toCities;
        filterSettings.generalSearchField = filterInfo.reportNumberSearch;
        filterSettings.reporters = filterInfo.reporter;
        filterSettings.validFrom = filterInfo.fromDate;
        filterSettings.validTo = filterInfo.toDate;
        filterSettings.properties = this.convertAnyArrayToProperties(filterInfo.properties);
        this.props.onSaveFilter(filterSettings, () => { });
    }

    private convertPropertiesToAnyArray(properties: SettingsProperties): any[] {
        const anyArr: any[] = [];
        if (properties.adr) {
            anyArr.push("adr");
        } if (properties.bulkGoods) {
            anyArr.push("bulkGoods");
        } if (properties.container20) {
            anyArr.push("container20");
        } if (properties.containerOver20) {
            anyArr.push("containerOver20");
        } if (properties.explosive) {
            anyArr.push("explosives");
        } if (properties.glass) {
            anyArr.push("glass");
        } if (properties.mega) {
            anyArr.push("mega");
        } if (properties.pieceGoods) {
            anyArr.push("pieceGoods");
        } if (properties.tank) {
            anyArr.push("tank");
        } if (properties.thermal) {
            anyArr.push("thermo");
        } if (properties.trailer) {
            anyArr.push("trailer");
        }
        return anyArr;
    }

    private convertAnyArrayToProperties(propertiesArr: any[]): SettingsProperties {
        const props: SettingsProperties = {
            adr: propertiesArr.findIndex(val => val === "adr") > -1 ? true : false,
            explosive: propertiesArr.findIndex(val => val === "explosives") > -1 ? true : false,
            thermal: propertiesArr.findIndex(val => val === "thermo") > -1 ? true : false,
            tank: propertiesArr.findIndex(val => val === "tank") > -1 ? true : false,
            glass: propertiesArr.findIndex(val => val === "glass") > -1 ? true : false,
            mega: propertiesArr.findIndex(val => val === "mega") > -1 ? true : false,
            containerOver20: propertiesArr.findIndex(val => val === "containerOver20") > -1 ? true : false,
            container20: propertiesArr.findIndex(val => val === "container20") > -1 ? true : false,
            pieceGoods: propertiesArr.findIndex(val => val === "pieceGoods") > -1 ? true : false,
            bulkGoods: propertiesArr.findIndex(val => val === "bulkGoods") > -1 ? true : false,
            trailer: propertiesArr.findIndex(val => val === "trailer") > -1 ? true : false
        };
        return props;
    }

    private handleSelectFilter(id: string) {
        const selected: FilterSettingsModel | undefined = this.props.filterSettings.data.find(x => x.id === id);
        if (selected) {
            const filter: UsedFilterSettingsModel = {
                isInUse: true,
                model: {
                    id: selected.id,
                    name: selected.name,
                    userId: selected.userId,
                    tracking: selected.tracking,
                    targetType: selected.targetType,
                    departureCityIds: selected.departureCityIds,
                    destinationCityIds: selected.destinationCityIds,
                    generalSearchField: selected.generalSearchField,
                    reporters: selected.reporters,
                    validFrom: selected.validFrom,
                    validTo: selected.validTo,
                    properties: selected.properties,
                    fromCityLocations: selected.fromCityLocations ?? this.state.allCities.filter((item) => selected.departureCityIds.includes(item.id)).map((item, index) => item) ?? [],
                    toCityLocations: selected.toCityLocations ?? this.state.allCities.filter((item) => selected.destinationCityIds.includes(item.id)).map((item, index) => item) ?? []
                }
            };
            localStorage.setItem("usedFilterSettingsModel", JSON.stringify(filter));

            const filters = this.getFilterSettingsModelIfEnabled(this.state);

            localStorage.setItem("commonFilters", JSON.stringify(filters));
            this.setState(filters);
            this.props.onFiltersChanged(filters);
        }
    }


    private copyFilterValuesOver(filter: FilterInfoExtraData): FilterInfoExtraData {
        const start = moment().startOf("day");

        const newFilter: FilterInfoExtraData = {
            filterMode: FilterMode.All,
            fromDate: start,
            toDate: start.clone().add(7, "days"),
            fromCities: [],
            toCities: [],
            properties: [],
            reporter: [],
            selectedReporters: [],
            reportNumberSearch: this.props.initialValues && this.props.initialValues.reportNumberSearch || "",
            error: "",
            fromCityLocations: [],
            toCityLocations: []
        };

        const filterString = localStorage.getItem("commonFilters");
        if (filterString) {
            const tmpFilters = JSON.parse(filterString, this.reviveFilters);
            newFilter.fromDate = moment(tmpFilters.fromDate || "");
            newFilter.toDate = moment(tmpFilters.toDate || "");
            newFilter.filterMode = tmpFilters.filterMode;
            newFilter.fromCities = tmpFilters.fromCities || [];
            newFilter.toCities = tmpFilters.toCities || [];
            newFilter.properties = tmpFilters.properties || [];
            newFilter.reporter = tmpFilters.reporter || [];
            newFilter.selectedReporters = this.getReportersFromIds(newFilter.reporter);
            newFilter.reportNumberSearch = tmpFilters.reportNumberSearch || (this.props.initialValues && this.props.initialValues.reportNumberSearch || "");
            newFilter.error = tmpFilters.error || "";
            newFilter.fromCityLocations = tmpFilters.fromCityLocations || [];
            newFilter.toCityLocations = tmpFilters.toCityLocations || [];
        }

        return newFilter;
    }

    private clearFilter() {
        localStorage.removeItem("usedFilterSettingsModel");
        localStorage.removeItem("commonFilters");

        const start = moment().startOf("day");

        const newFilter: FiltersState = {
            filterMode: FilterMode.All,
            fromDate: start,
            toDate: start.clone().add(7, "days"),
            fromCities: [],
            toCities: [],
            properties: [],
            reporter: [],
            selectedReporters: [],
            reportNumberSearch: this.props.initialValues && this.props.initialValues.reportNumberSearch || "",
            error: "",
            selectedFilterSetting: null,
            fromCityLocations: [],
            toCityLocations: []
        };

        localStorage.setItem("commonFilters", JSON.stringify(newFilter));
        this.setState(newFilter);
        this.props.onFiltersChanged(newFilter);
    }

    private isSaveFilterEnabled(): boolean {
        const currFiltterSettingsModel = localStorage.getItem("usedFilterSettingsModel");
        if (currFiltterSettingsModel) {
            const currSettings = JSON.parse(currFiltterSettingsModel);
            if (currSettings && currSettings.isInUse) {
                return false;
            }
        }
        return true;
    }
}

export default FilterPanel;




// WEBPACK FOOTER //
// ./src/components/tables/FilterPanel.tsx